import { useRef, useState } from 'react';
import { CLASSE_SURVOL, useListeClavier } from '../hooks/useListeClavier';
import { nomPersonnel } from '../lib/personnel';
import type { Personnel } from '../types';

/**
 * Choix d'un personnel, a la saisie.
 *
 * Une liste deroulante ne suffisait plus pour deux raisons :
 *   - elle n'affichait que le PRENOM d'usage, et deux personnel
 *     s'appellent Jeremy — impossible de savoir lequel on choisit ;
 *   - a vingt-six fiches, derouler devient plus long que taper.
 *
 * On saisit donc, et la liste se filtre. Le champ reste un <input>
 * ordinaire : la valeur retenue est un identifiant, jamais le texte tape,
 * pour qu'un homonyme ne puisse pas etre choisi par accident.
 */
export function ChoixPersonnel({
  personnel,
  valeur,
  onChanger,
  placeholder = 'Taper un nom…',
  exclure,
  desactive = false,
}: {
  personnel: Personnel[];
  valeur: string;
  onChanger: (id: string) => void;
  placeholder?: string;
  /** Identifiant a retirer de la liste — le principal, cote assistant. */
  exclure?: string;
  desactive?: boolean;
}) {
  const [recherche, setRecherche] = useState('');
  const [ouvert, setOuvert] = useState(false);
  const conteneur = useRef<HTMLDivElement>(null);

  const choisi = personnel.find((p) => p.id === valeur) ?? null;

  // Toutes les fiches sont proposables : il n'y a plus de drapeau
  // `actif` — un personnel sorti est celui dont le contrat est clos, et
  // c'est la derivation de paie qui signalera un creneau sans contrat.
  const candidats = personnel
    .filter((p) => p.id !== exclure)
    .filter((p) => {
      if (!recherche.trim()) return true;
      const terme = recherche.trim().toLowerCase();
      return nomPersonnel(p).toLowerCase().includes(terme);
    })
    .slice(0, 12);

  const choisir = (p: Personnel) => {
    onChanger(p.id);
    setRecherche('');
    setOuvert(false);
  };
  const clavier = useListeClavier(candidats, choisir);

  // Une fiche choisie s'affiche comme une pastille : le champ de saisie
  // ne sert qu'a chercher, et garder le texte tape apres coup laisserait
  // croire qu'il vaut selection.
  if (choisi && !ouvert) {
    return (
      <div className="flex items-center gap-2">
        <span className="champ flex flex-1 items-center justify-between gap-2">
          <span className="truncate">
            {choisi.prenom_usuel} <span className="uppercase">{choisi.nom ?? ''}</span>
          </span>
          {!desactive && (
            <button
              type="button"
              className="shrink-0 text-sm text-encre-500 underline"
              onClick={() => onChanger('')}
            >
              Retirer
            </button>
          )}
        </span>
        {!desactive && (
          <button
            type="button"
            className="shrink-0 text-sm text-encre-500 underline"
            onClick={() => {
              setRecherche('');
              setOuvert(true);
            }}
          >
            Changer
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={conteneur}>
      <input
        className="champ"
        value={recherche}
        placeholder={placeholder}
        disabled={desactive}
        onChange={(evenement) => {
          setRecherche(evenement.target.value);
          setOuvert(true);
        }}
        onFocus={() => setOuvert(true)}
        onKeyDown={clavier.auClavier}
        // Le clic sur une option doit precéder la fermeture, sinon il ne
        // l'atteint jamais.
        onBlur={() => window.setTimeout(() => setOuvert(false), 150)}
      />

      {ouvert && (
        <ul
          ref={clavier.liste}
          className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-encre-200 bg-white shadow-lg"
        >
          {candidats.length === 0 ? (
            <li className="px-3 py-2 text-sm text-encre-400">Aucune fiche ne correspond.</li>
          ) : (
            candidats.map((personnel, rang) => (
              <li key={personnel.id} data-index={rang}>
                <button
                  type="button"
                  className={`flex w-full items-baseline gap-2 px-3 py-2 text-left text-sm ${
                    rang === clavier.index ? CLASSE_SURVOL : ''
                  }`}
                  onMouseEnter={() => clavier.survoler(rang)}
                  onClick={() => choisir(personnel)}
                >
                  <span>{personnel.prenom_usuel}</span>
                  <span className="uppercase text-encre-500">{personnel.nom ?? ''}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
